import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

interface Kod { kod: string; nev: string; }
interface Papir { id: string; nyomtatvany_tipus: string; sorozat: string; sorszam: string; tanev: number; allapot: string; ok: string | null; }
interface Tanev {
  osztaly_id: string; osztaly: string; tanev: number; evfolyam: number; torzslapszam: string | null; tanev_cimke: string;
  digitalis: { id: string; nev: string; letrehozva: string; path: string } | null; papir: Papir[];
}
interface TanuloAdat {
  tanulo: { oktazon: string; nev: string };
  tanevek: Tanev[];
  masodlatok: any[];
  okok: Kod[]; sorsok: Kod[];
  alap: { kiallito_nev: string; kiallito_cim: string; kiadas_napja: string };
  iktatgato: boolean;
}

/**
 *  Admin → Bizonyítványmásodlat (backend/bizonyitvany-masodlat.js), 20/2012. EMMI r. 113. § (2)–(3), 114. §.
 *
 *  Egy tanuló egy tanévére: a tárolt év végi adatokból (ugyanaz a forrás és sablon, mint a bizonyítványé)
 *  „BIZONYÍTVÁNYMÁSODLAT” jelöléssel és záradékkal (kiadás oka, kiállító neve és címe, kiadás napja,
 *  iktatószám), aláírva, időbélyeggel; iktatva (Iktatgató, ha fut) és a törzslapra bejegyezve. Ha az
 *  eredeti papír bizonyítvány a szigorú számadásban szerepel, itt be is vonható. Jog: admin „masodlat”.
 */
@Component({
  selector: 'app-admin-bizonyitvany-masodlat',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './bizonyitvany-masodlat.component.html',
  styleUrls: ['./bizonyitvany-masodlat.component.scss'],
})
export class AdminBizonyitvanyMasodlatComponent implements OnInit {
  intezmeny = '';
  kereses = '';
  talalatok: any[] = [];
  keres = false;
  adat: TanuloAdat | null = null;
  lista: any[] = [];
  fut = false;
  hiba = '';

  /** A kiállítás űrlapja. */
  u = {
    tanevKulcs: '', ok: '', ok_reszletek: '', eredeti_sorsa: 'ervenytelenitve', eredeti_sorszam: '',
    papir_bevonas: '', kiallito_nev: '', kiallito_cim: '', kiadas_napja: '', iktatoszam: '', eredeti_keltezes: '',
  };
  /** Az imént kiállított másodlat (link a PDF-re). */
  kesz: { path: string; iktatoszam: string; figyelmeztetes: string[] } | null = null;

  private keresIdozito: any = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.intezmeny = String((await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.listaBetolt();
  }

  async listaBetolt() {
    try {
      this.lista = await firstValueFrom(this.http.get<any[]>(`/api/bizonyitvany-masodlat/lista/${this.intezmeny}`)) || [];
    } catch (e: any) {
      this.lista = [];
      if (e?.status === 403) this.hiba = e?.error?.error || 'Ehhez adminisztrátori jogosultság kell (Bizonyítványmásodlat).';
    }
  }

  keresesValtozott() {
    clearTimeout(this.keresIdozito);
    const k = this.kereses.trim();
    if (k.length < 2) { this.talalatok = []; return; }
    this.keresIdozito = setTimeout(async () => {
      this.keres = true;
      try {
        this.talalatok = await firstValueFrom(this.http.get<any[]>(`/api/bizonyitvany-masodlat/tanulok/${this.intezmeny}`, { params: { q: k } })) || [];
      } catch { this.talalatok = []; } finally { this.keres = false; }
    }, 300);
  }

  async valaszt(t: any) {
    this.talalatok = [];
    this.kereses = t.nev;
    this.kesz = null;
    this.fut = true; this.hiba = '';
    try {
      this.adat = await firstValueFrom(this.http.get<TanuloAdat>(`/api/bizonyitvany-masodlat/tanulo/${this.intezmeny}`, { params: { oktazon: t.oktazon } }));
      const a = this.adat;
      this.u = {
        tanevKulcs: '', ok: '', ok_reszletek: '', eredeti_sorsa: 'ervenytelenitve', eredeti_sorszam: '', papir_bevonas: '',
        kiallito_nev: a?.alap?.kiallito_nev || '', kiallito_cim: a?.alap?.kiallito_cim || '',
        kiadas_napja: a?.alap?.kiadas_napja || '', iktatoszam: '', eredeti_keltezes: '',
      };
    } catch (e: any) {
      this.adat = null;
      this.hiba = e?.error?.error || 'A tanuló adatai nem tölthetők be.';
    } finally {
      this.fut = false;
    }
  }

  get tanev(): Tanev | null {
    return this.adat?.tanevek.find((t) => `${t.tanev}|${t.osztaly_id}` === this.u.tanevKulcs) || null;
  }

  /** Tanév-váltáskor az eredeti papír bizonyítvány sorszáma és a bevonás javaslata. */
  tanevValtozott() {
    const t = this.tanev;
    const kiadott = (t?.papir || []).find((p) => p.allapot === 'kiadott') || null;
    this.u.papir_bevonas = kiadott ? kiadott.id : '';
    this.u.eredeti_sorszam = kiadott ? `${kiadott.sorozat ? kiadott.sorozat + ' ' : ''}${kiadott.sorszam}` : '';
  }

  get kiadottPapir(): Papir[] {
    return (this.tanev?.papir || []).filter((p) => p.allapot === 'kiadott');
  }

  get kiallithato(): boolean {
    const u = this.u;
    return !!(this.adat && this.tanev && u.ok && (u.ok !== 'egyeb' || u.ok_reszletek.trim()) && u.eredeti_sorsa
      && u.kiallito_nev.trim() && u.kiallito_cim.trim() && /^\d{4}-\d{2}-\d{2}$/.test(u.kiadas_napja)
      && (this.adat.iktatgato || u.iktatoszam.trim()));
  }

  async kiallit() {
    const t = this.tanev;
    if (!this.adat || !t || !this.kiallithato || this.fut) return;
    if (!confirm(`Bizonyítványmásodlatot állítasz ki: ${this.adat.tanulo.nev}, ${t.tanev_cimke} (${t.osztaly}).\n`
      + 'A másodlat aláírva a dokumentumtárba kerül, iktatódik, és a törzslapra bejegyzés készül. Folytatod?')) return;
    this.fut = true; this.kesz = null;
    this.data.showLoading('Bizonyítványmásodlat készítése, aláírása…');
    try {
      const u = this.u;
      const r: any = await firstValueFrom(this.http.post(`/api/bizonyitvany-masodlat/${this.intezmeny}`, {
        oktazon: this.adat.tanulo.oktazon, osztaly: t.osztaly_id, ev: String(t.tanev),
        ok: u.ok, ok_reszletek: u.ok_reszletek.trim(), eredeti_sorsa: u.eredeti_sorsa, eredeti_sorszam: u.eredeti_sorszam.trim(),
        papir_bevonas: u.papir_bevonas || undefined, eredeti_dokumentum: t.digitalis?.id || undefined,
        kiallito_nev: u.kiallito_nev.trim(), kiallito_cim: u.kiallito_cim.trim(), kiadas_napja: u.kiadas_napja,
        iktatoszam: u.iktatoszam.trim() || undefined, eredeti_keltezes: u.eredeti_keltezes || undefined,
      }));
      this.kesz = { path: r?.path || '', iktatoszam: r?.iktatoszam || '', figyelmeztetes: r?.figyelmeztetes || [] };
      this.data.sendToast(`Bizonyítványmásodlat kiállítva (iktatószám: ${r?.iktatoszam}).`, this.kesz.figyelmeztetes.length ? 'warning' : 'success');
      await this.valaszt({ oktazon: this.adat.tanulo.oktazon, nev: this.adat.tanulo.nev });
      this.kesz = { path: r?.path || '', iktatoszam: r?.iktatoszam || '', figyelmeztetes: r?.figyelmeztetes || [] };
      this.listaBetolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A másodlat nem készült el.', 'error');
    } finally {
      this.data.hideLoading();
      this.fut = false;
    }
  }

  fajlUrl(path: string): string { return '/api/file/' + path; }

  /** 'YYYY-MM-DD' → 'YYYY. MM. DD.' */
  datum(d: string | null | undefined): string {
    return /^\d{4}-\d{2}-\d{2}/.test(String(d || '')) ? `${d!.slice(0, 4)}. ${d!.slice(5, 7)}. ${d!.slice(8, 10)}.` : '–';
  }
}
